import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'balmart_admin_token';

function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable — rely on cookie only */ }
}
function adminAuthHeaders() {
  const t = getStoredToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: adminAuthHeaders(),
      });
      setUser(res.data);
      // Sliding session — refresh stored token
      if (res.data?.session_token) setStoredToken(res.data.session_token);
    } catch {
      setUser(null);
      setStoredToken('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const setUserAndToken = (userData) => {
    setUser(userData);
    if (userData?.session_token) setStoredToken(userData.session_token);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
        headers: adminAuthHeaders(),
      });
    } catch { /* ignore */ }
    setStoredToken('');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser: setUserAndToken, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
